import { useRef, useState, useEffect } from "react";
import { Star, ExternalLink } from "lucide-react";
import { Button } from "../components/ui/button";
import { Card, CardContent } from "../components/ui/card";
import Autoplay from "embla-carousel-autoplay";
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselPrevious,
  CarouselNext,
} from "../components/ui/carousel";
import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOOGLE_MAPS_URL = "https://maps.app.goo.gl/C8s7vSJPZ2sHXxe16";

function GoogleLogo() {
  return (
    <svg viewBox="0 0 24 24" className="w-6 h-6" xmlns="http://www.w3.org/2000/svg">
      <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4"/>
      <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
      <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
      <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
    </svg>
  );
}

function StarRating({ rating }) {
  return (
    <div className="flex gap-0.5">
      {Array.from({ length: 5 }).map((_, i) => (
        <Star key={i} className={`w-4 h-4 ${i < rating ? "google-star" : "text-gray-200 fill-gray-200"}`} />
      ))}
    </div>
  );
}

function getInitials(name) {
  return name.split(" ").map(w => w[0]).join("").toUpperCase().slice(0, 2);
}

function ReviewCard({ review }) {
  return (
    <Card className="bg-white border border-gray-100 rounded-xl shadow-sm hover:shadow-md transition-shadow h-full">
      <CardContent className="p-5 md:p-7">
        <div className="flex items-center gap-3 mb-3">
          {review.photo ? (
            <img src={review.photo} alt={review.name} className="w-9 h-9 rounded-full object-cover" />
          ) : (
            <div className="w-9 h-9 bg-[#6AB023] rounded-full flex items-center justify-center text-white font-bold text-xs">
              {getInitials(review.name)}
            </div>
          )}
          <div className="flex-1 min-w-0">
            <p className="font-semibold text-gray-900 text-sm">{review.name}</p>
            <p className="text-xs text-gray-400">{review.date}</p>
          </div>
          <GoogleLogo />
        </div>
        <StarRating rating={review.rating} />
        <p className="text-sm text-gray-700 leading-relaxed mt-3">{review.text}</p>
      </CardContent>
    </Card>
  );
}

export default function GoogleReviewsSection() {
  const [reviews, setReviews] = useState([]);
  const [rating, setRating] = useState(5);
  const [total, setTotal] = useState(0);
  const [loaded, setLoaded] = useState(false);

  const autoplayPlugin = useRef(
    Autoplay({ delay: 5000, stopOnInteraction: true, stopOnMouseEnter: true })
  );

  useEffect(() => {
    axios.get(`${API}/google-reviews`)
      .then(res => {
        setReviews(res.data.reviews);
        setRating(res.data.rating);
        setTotal(res.data.total);
        setLoaded(true);
      })
      .catch(() => setLoaded(true));
  }, []);

  return (
    <section id="avis-google" data-testid="google-reviews-section" className="py-16 md:py-24 bg-[#F9FAFB]">
      <div className="container mx-auto px-4 md:px-6 max-w-7xl">
        <div className="text-center mb-10 md:mb-14 scroll-reveal">
          <div className="inline-flex items-center gap-2 mb-4">
            <GoogleLogo />
            <span className="text-sm font-medium text-gray-500">Avis Google</span>
          </div>
          <h2 className="text-3xl md:text-4xl font-bold tracking-tight text-[#6AB023] mb-4" data-testid="google-reviews-title">Ce que disent nos clients</h2>
          <div className="flex items-center justify-center gap-3 mt-4">
            <span className="text-4xl font-bold text-gray-900">{rating.toFixed(1)}</span>
            <div>
              <StarRating rating={Math.round(rating)} />
              <p className="text-xs text-gray-500 mt-1">{total} avis sur Google</p>
            </div>
          </div>
        </div>

        {loaded && reviews.length > 0 && (
          <>
            <div className="hidden md:grid md:grid-cols-3 gap-8 mb-10">
              {reviews.slice(0, 3).map((review, idx) => (
                <div key={review.name} className="animate-fade-in-up" style={{ animationDelay: `${idx * 0.12}s` }} data-testid={`google-review-${idx}`}>
                  <ReviewCard review={review} />
                </div>
              ))}
            </div>

            <div className="md:hidden mb-10 px-2">
              <Carousel opts={{ align: "start", loop: true }} plugins={[autoplayPlugin.current]} className="w-full" data-testid="reviews-carousel-mobile">
                <CarouselContent className="-ml-3">
                  {reviews.map((review, idx) => (
                    <CarouselItem key={review.name} className="pl-3 basis-[88%]">
                      <div data-testid={`google-review-mobile-${idx}`}>
                        <ReviewCard review={review} />
                      </div>
                    </CarouselItem>
                  ))}
                </CarouselContent>
              </Carousel>
            </div>
          </>
        )}

        {/* Bouton voir tous les avis */}
        <div className="text-center mt-4">
          <a href={GOOGLE_MAPS_URL} target="_blank" rel="noreferrer">
            <Button variant="outline" className="border-[#6AB023] text-[#6AB023] hover:bg-green-50 rounded-full px-8 h-11 font-semibold gap-2">
              <GoogleLogo />
              Voir nos {total > 0 ? total : ""} avis Google
              <ExternalLink className="w-4 h-4" />
            </Button>
          </a>
        </div>

      </div>
    </section>
  );
}
